<template>
  <div class="h-full">
    <div class="h-full flex flex-col">
      <div class="flex w-full pb-6 items-center flex-shrink-0 sm:pr-10 pl-2 h-14">
        <div class="flex-grow flex items-center">
          <div class="text-13 text-primary font-600">{{ $t('projects.email_providers.title') }}</div>
        </div>
      </div>
      <div class="w-full pr-3 flex-grow overflow-auto sm:pr-10 pl-2 pb-2">
        <div class="pl-1 font-500 mb-2 text-13">Active</div>
        <template v-if="Object.keys(emailProviders).length">
          <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 pt-1">
            <ListCard v-for="(provider, index) in emailProviders" :key="`${index}-${provider}`" class="h-20 py-2 bg-white rounded-20 cursor-pointer" @click="onEditClicked(provider)">
              <div class="flex pl-4 pr-2 items-center text-14 font-500 text-gray-900 truncate">
                <div class="mr-3"><img :src="`/img/providers/${getIcon(provider.provider)}`" class="h-6" /></div>
                <div>{{ provider.name }}</div>
              </div>
            </ListCard>
          </div>
        </template>
        <div v-else class="flex flex-col flex-grow items-center justify-center">
          <div class="my-1 text-14 text-gray-700">{{ $t('projects.email_providers.have_no_providers') }}</div>
          <div class="my-1 text-12 text-gray-600">{{ $t('projects.email_providers.create_first_provider') }}</div>
        </div>
        <div class="pl-1 font-500 mb-2 mt-6 text-13">Available</div>
        <template v-if="Object.keys(availableProviders).length">
          <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 pt-1">
            <div v-for="(provider, key) in availableProviders" :key="key" class="relative">
              <div v-if="!provider.enabled" class="absolute right-0 z-1 top-0 text-10 font-500 tracking-wide bg-gray-500 text-off-white px-3 rounded-tr-20 rounded-bl-20">Soon</div>
              <ListCard
                :class="{
                  'cursor-pointer': provider.enabled,
                  'opacity-50': !provider.enabled,
                }"
                class="h-20 py-2 bg-white rounded-20 relative select-none"
                @click="provider.enabled && onCreateClicked(provider)"
              >
                <div class="flex justify-between items-center w-full flex-wrap -mt-3">
                  <div class="flex items-center mt-3">
                    <img :src="`/img/providers/${getIcon(provider.key)}`" class="w-7 h-7 mr-4 ml-2 flex-shrink-0" :alt="provider.name" />
                    <div class="flex items-center truncate">
                      <span class="font-500 mr-2 truncate">{{ provider.name }}</span>
                    </div>
                  </div>
                </div>
              </ListCard>
            </div>
          </div>
        </template>
      </div>
    </div>
    <Modal v-if="fetching">
      <div class="flex flex-col font-700 items-center justify-center px-20 my-10">
        <div>{{ $t('loading_data') }}</div>
        <Icon name="loading_dots" class="w-14 mt-3 text-primary" />
      </div>
    </Modal>
  </div>
</template>
<script>
import { mapActions, mapState } from 'vuex';
import CreateEmailProviderModal from '@/components/project/CreateEmailProviderModal.vue';
import DeleteModal from '../../modals/DeleteModal.vue';

export default {
  name: 'EmailProviders',
  data() {
    return {
      fetching: false,
    };
  },
  computed: {
    ...mapState(['projectData', 'availableEmailProviders']),
    emailProviders() {
      return Object.values(this.projectData[this.projectId]?.emailProviders || {}).map((item) => {
        return {
          ...item,
          name: this.availableEmailProviders[item.provider].name,
        };
      });
    },
    projectId() {
      return this.$route.params.projectId;
    },
    availableProviders() {
      const availableProviders = structuredClone(this.availableEmailProviders || {});
      this.emailProviders.forEach((provider) => {
        delete availableProviders[provider.provider];
      });
      return availableProviders;
    },
  },
  methods: {
    ...mapActions(['showToastMessage', 'fetchProjectEmailProviders', 'deleteProjectEmailProvider']),
    getIcon(name) {
      const icon = {
        AWS_SES: 'AWS_SES.png',
        SENDGRID: 'SENDGRID.png',
      };
      return icon?.[name] || '';
    },
    onCreateClicked(item) {
      this.$showModal(CreateEmailProviderModal, { projectId: this.projectId, provider: item.key, providerName: item.name });
    },
    onEditClicked(item) {
      const modal = this.$showModal(CreateEmailProviderModal, {
        projectId: this.projectId,
        editData: item,
        provider: item.provider,
        providerName: item.name,
        onDelete: async () => {
          await this.onDeleteClicked(item, modal);
        },
      });
    },
    onDeleteClicked(item, modal) {
      this.$showModal(DeleteModal, {
        subtitle: this.$t('projects.email_providers.remove_provider', { templateName: item.name }),
        info: null,
        onConfirm: async () => {
          await this.onDeleteConfirmClicked(item);
          modal.close();
        },
      });
    },
    async onDeleteConfirmClicked(item) {
      await this.deleteProjectEmailProvider({ project_id: this.projectId, provider: item.provider });
    },
  },
  watch: {
    projectId: {
      async handler() {
        this.fetching = true;
        await this.fetchProjectEmailProviders({ projectId: this.$route.params.projectId });
        this.fetching = false;
      },
      immediate: true,
    },
  },
};
</script>
